import { useEffect, useId, useState } from "react";
import { Input } from "@guestpilot/ui-kit";
import { useGet } from "../api/hooks";
import type { PersonRef } from "../api/types";
import { useI18n } from "../i18n";

/** Type-ahead over the tenant's internal people (BR-009, BR-050): sponsors and reviewers are never external users. */
export function PersonPicker({
  value,
  onChange,
  label,
  id,
}: {
  value: PersonRef | null;
  onChange: (p: PersonRef | null) => void;
  label: string;
  id?: string;
}) {
  const { t } = useI18n();
  const listId = useId();
  const [text, setText] = useState(value?.displayName ?? "");
  const [debounced, setDebounced] = useState("");
  useEffect(() => setText(value?.displayName ?? ""), [value]);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(text), 200);
    return () => clearTimeout(h);
  }, [text]);
  const search = useGet<PersonRef[]>(
    "/api/v1/directory/people",
    { q: debounced },
    { enabled: debounced.length >= 2 && debounced !== value?.displayName },
  );
  return (
    <div className="gp-stack" style={{ gap: "var(--space-4)" }}>
      <Input
        id={id}
        role="combobox"
        aria-label={label}
        aria-expanded={(search.data ?? []).length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        value={text}
        placeholder={t("ui:picker.placeholder")}
        onChange={(e) => {
          setText(e.target.value);
          if (value) onChange(null);
        }}
      />
      {debounced.length >= 2 && !value && (search.data ?? []).length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="gp-panel"
          style={{ listStyle: "none", margin: 0, padding: 0 }}
        >
          {(search.data ?? []).map((p) => (
            <li
              key={p.id}
              role="option"
              aria-selected={false}
              tabIndex={0}
              className="gp-masteritem"
              onClick={() => onChange(p)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(p);
                }
              }}
            >
              <span className="gp-person-text">
                <span className="t-body-strong">{p.displayName}</span>
                {p.email ? <span className="t-body-sm gp-faint">{p.email}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
